import {afterNextRender, computed, inject, Injectable, Injector, signal} from '@angular/core';
import {Tabs} from 'primeng/tabs';
import {RegisteredSaveButton, TabLocation} from './save-button-tracking.types';

@Injectable({
  providedIn: 'root',
})
export class SaveButtonTrackingService {
  private readonly injector = inject(Injector);

  private readonly activeSaveButtons = signal<Map<string, RegisteredSaveButton>>(new Map());
  private readonly revealRequestCount = signal(0);

  readonly hasSaveButtons = computed(() => this.activeSaveButtons().size > 0);
  readonly revealRequests = this.revealRequestCount.asReadonly();

  requestReveal(): void {
    this.revealRequestCount.update((count) => count + 1);

    afterNextRender(() => this.scrollToFirstVisibleButton(), {injector: this.injector});
  }

  registerButton(
    id: string,
    button: RegisteredSaveButton = {element: null, enclosingTabs: []},
  ): void {
    this.activeSaveButtons.update((buttons) => new Map(buttons).set(id, button));
  }

  unregisterButton(id: string): void {
    this.activeSaveButtons.update((buttons) => {
      const remainingButtons = new Map(buttons);
      remainingButtons.delete(id);
      return remainingButtons;
    });
  }

  clearSaveButtons(): void {
    this.activeSaveButtons.set(new Map());
  }

  hasUnsavedChanges(tabs: Tabs, tabValue: string): boolean {
    return this.unsavedTabLocations().some(
      (location) => location.tabs === tabs && location.tabValue === tabValue,
    );
  }

  firstUnsavedTabValue(tabs: Tabs): string | undefined {
    return this.unsavedTabLocations().find((location) => location.tabs === tabs)?.tabValue;
  }

  private unsavedTabLocations(): TabLocation[] {
    return [...this.activeSaveButtons().values()].flatMap((button) => button.enclosingTabs);
  }

  private scrollToFirstVisibleButton(): void {
    const visibleButton = [...this.activeSaveButtons().values()].find(
      (button) => button.element !== null && button.element.offsetParent !== null,
    );

    visibleButton?.element?.scrollIntoView({behavior: 'smooth', block: 'center'});
  }
}
